/* UseSignRedirectAnalytics is imported from basic on assumption that signup link is only used in logged out mode.
 * ticket to fix this has been raised: https://bridebook.atlassian.net/browse/LIVE-19187
 */
import useSignAnalytics from '@bridebook/web/app-basic/lib/hooks/use-sign-analytics';
import { FunctionComponent, memo, MouseEventHandler, ReactNode } from 'react';
import { A, FelaCSS } from '@bridebook/ui';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import { TLoggedOutPageClickedSection } from '@bridebook/web/app-shared/lib/types';
import { isSearchResultsPage } from '@bridebook/web/lib/search/utils';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { createReverseMappingForEnum } from '@bridebook/web/lib/utils';
import {
  isLoggedOutToolPage,
  isSupplierPage,
  LoggedOutToolPaths,
  usePathWithoutMarket,
} from '@bridebook/web/lib/utils/url';

export interface SignupLinkParams {
  isLoggedIn?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
  style?: FelaCSS;
  showLogin?: boolean;
  children?: ReactNode;
  query?: string;
  clickedLocation?: string;
  clickedSection?: TLoggedOutPageClickedSection;
  cta?: string;
  // Used if analytics are handled outside the component
  noAnalytics?: boolean;
  /**
   * Whether the query should be appended to the URL for all signup links,
   * or only for signup links outside of supplier pages.
   *
   * @default true
   */
  queryOnlyForNonSuppliers?: boolean;
}

const SignupLink: FunctionComponent<SignupLinkParams> = ({
  isLoggedIn,
  showLogin,
  children,
  onClick,
  style = {},
  clickedLocation,
  clickedSection,
  query = '',
  cta,
  noAnalytics,
  queryOnlyForNonSuppliers = true,
}) => {
  const baseUrl = showLogin ? UrlHelper.login : UrlHelper.signup;
  const pathname = usePathWithoutMarket();

  const parseClickLocation = () => {
    if (isSupplierPage(pathname)) return 'supplierProfile';
    if (isSearchResultsPage(pathname)) return 'search';
    if (isLoggedOutToolPage(pathname))
      return createReverseMappingForEnum(LoggedOutToolPaths)[pathname];
    return pathname;
  };

  const analyticsHandler = isLoggedIn
    ? undefined
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useSignAnalytics(
        clickedLocation ? clickedLocation : parseClickLocation(),
        clickedSection ? clickedSection : 'header',
        cta ? cta : 'Sign up',
      );

  const linkUrl =
    queryOnlyForNonSuppliers && isSupplierPage(pathname) ? baseUrl : `${baseUrl}${query}`;

  const link = (
    <Link href={linkUrl} passHref legacyBehavior>
      <A rel="nofollow" noUnderline display="block" style={{ display: 'block', ...style }}>
        {children}
      </A>
    </Link>
  );

  // With onClick handler for loggedIn users
  return isLoggedIn && onClick ? (
    <div onClick={onClick}>{children}</div>
  ) : clickedLocation && !isLoggedIn ? (
    <div onClick={noAnalytics ? undefined : analyticsHandler}>{link}</div>
  ) : (
    link
  );
};
export default memo(SignupLink);
